import React from "react";
import styles from "./InTaxIncomeSection.module.css";
import InTaxIncomeSummary from "../InTaxIncomeSummary/InTaxIncomeSummary";
import InTaxPaystubsTable from "../InTaxPaystubsTable/InTaxPaystubsTable";
import AddPaystubModal from "../AddPaystubModal/AddPaystubModal";
import { ensureKvArray } from "../../utils/keyValue";

const fmt = new Intl.NumberFormat(undefined, { style: "currency", currency: "CAD" }).format;

export default function InTaxIncomeSection({
  source, defaultOpen = false, configIndex, year, jurisdiction, userEmail = "",
}) {
  const [open, setOpen] = React.useState(defaultOpen);
  const [showModal, setShowModal] = React.useState(false);
  const [rows, setRows] = React.useState([]); // store the *doc* shape here

  const normalizeEmail = (e) => String(e || "").trim().toLowerCase();

  // Single add: modal already returns a *doc*, but we ensure KV arrays and add identifiers
  const handleCreateDoc = (docLike) => {
    const doc = {
      ...docLike,
      id: docLike.id || `tmp-${cryptoRandom()}`,
      userEmail: normalizeEmail(userEmail),
      sourceId: source.id,
      sourceName: source.name,
      jurisdiction: {
        countryCode: jurisdiction.countryCode,
        provinceOrState: jurisdiction.provinceOrState,
        filingStatus: jurisdiction.filingStatus,
        year,
        ...(docLike?.jurisdiction || {}),
      },
      taxes: ensureKvArray(docLike.taxes),
      otherDeductions: ensureKvArray(docLike.otherDeductions),
    };

    setRows((r) => [doc, ...r]);
    setShowModal(false);
  };

  // Bulk import: modal gives array of docs (already normalized), we just stamp user/source info
  const handleImportDocs = (arr) => {
    const stamped = (arr || []).map((d) => ({
      ...d,
      id: d.id || `imp-${cryptoRandom()}`,
      userEmail: normalizeEmail(userEmail),
      sourceId: source.id,
      sourceName: source.name,
      jurisdiction: {
        countryCode: jurisdiction.countryCode,
        provinceOrState: jurisdiction.provinceOrState,
        filingStatus: jurisdiction.filingStatus,
        year,
        ...(d?.jurisdiction || {}),
      },
      taxes: ensureKvArray(d.taxes),
      otherDeductions: ensureKvArray(d.otherDeductions),
    }));
    setRows((r) => [...stamped, ...r]);
    setShowModal(false);
  };

  const y = { gross: 0, withheld: 0, other: 0, takeHome: 0 }; // can compute from rows later

  return (
    <div className={`card mb-3 ${styles.section}`}>
      <div className={`card-header d-flex align-items-center justify-content-between ${styles.header}`}>
        <button
          type="button"
          className={`btn btn-link text-decoration-none p-0 ${styles.toggle}`}
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
        >
          <span className="me-2">{open ? "▾" : "▸"}</span>
          <strong>{source.name}</strong>
          <span className="ms-2 text-secondary small">· {rows.length} stubs</span>
        </button>

        {!open && (
          <div className={`d-none d-md-flex align-items-center gap-3 ${styles.mini}`}>
            <MiniStat label="Gross" value={y.gross} />
            <MiniStat label="Withheld" value={y.withheld} />
            <MiniStat label="Take-home" value={y.takeHome} />
          </div>
        )}

        <div className="d-flex align-items-center gap-2 ms-3">
          <button className="btn btn-outline-primary btn-compact" onClick={() => setShowModal(true)}>
            Add pay stub
          </button>
        </div>
      </div>

      {open && (
        <div className="card-body">
          <InTaxIncomeSummary ytd={y} />
          <div className="mt-3">
            <InTaxPaystubsTable rows={rows} />
          </div>
        </div>
      )}

      {showModal && (
        <AddPaystubModal
          onClose={() => setShowModal(false)}
          onSubmit={handleCreateDoc}     // single returns one Firestore doc shape
          onImport={handleImportDocs}    // csv returns array of Firestore doc shapes
          configIndex={configIndex}
          year={year}
          jurisdiction={jurisdiction}
          onJurisdictionChange={() => {}}
          userEmail={userEmail}
          source={source}
        />
      )}
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className={styles.miniStat} title={label}>
      <span className="text-secondary small me-1">{label}:</span>
      <span className="fw-semibold">{fmt(value || 0)}</span>
    </div>
  );
}

function cryptoRandom() {
  try { return crypto.getRandomValues(new Uint32Array(1))[0].toString(36); }
  catch { return Math.random().toString(36).slice(2); }
}
